import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabaseClient } from '../../lib/supabase';
import { KeywordComparisonTable } from './KeywordComparisonTable';
import { LoadingScreen } from '../common/LoadingScreen';
import { RefreshCw, Save } from 'lucide-react';
import { toast } from '../../lib/toast';
import { analyzeKeywords } from '../../lib/text-analysis';
import { isStopWord, normalizeText as normalizeTextFromLib } from '../../lib/stop-words';

interface TextAnalysisTabProps {
  analysisId: string;
  contentAnalysis: any;
}

// Helper function to normalize text
async function normalizeText(text: string, language: string = 'uk'): Promise<string> {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  return await normalizeTextFromLib(text, language);
}

// Helper function to prepare keyword comparison data
async function prepareKeywordComparisonData(contentAnalysis: any, additionalKeywords: string[] = [], language: string = 'uk') {
  if (!contentAnalysis?.competitors) {
    return { competitors: [], keywords: [] as string[], additionalKeywords: new Set<string>() };
  }

  const selectedDomains = contentAnalysis.selectedDomains || [];
  const allKeywords = new Set<string>();
  const competitors: Array<{
    domain: string;
    url: string;
    keywords: Array<{
      keyword: string;
      frequency: number;
      tfIdf: number;
      density: number;
    }>;
  }> = [];

  // Process additional keywords
  const additionalKeywordsSet = new Set<string>();
  for (const keyword of additionalKeywords) {
    const normalizedKeyword = await normalizeText(keyword, language);
    if (normalizedKeyword && !(await isStopWord(normalizedKeyword, language))) {
      allKeywords.add(normalizedKeyword);
      additionalKeywordsSet.add(normalizedKeyword);
    }
  }

  // Process our content
  if (selectedDomains.includes('our') && contentAnalysis.our_domain) {
    const ourText = [
      ...Object.values(contentAnalysis.our_domain.headers || {}).flat(),
      ...(contentAnalysis.our_domain.texts || [])
    ].join(' ').trim();

    if (ourText) {
      const wordCounts = new Map<string, number>();
      const normalizedText = await normalizeText(ourText, language);
      const words = normalizedText.split(/\s+/);
      const totalWords = words.length;

      for (const word of words) {
        if (word && !(await isStopWord(word, language))) {
          allKeywords.add(word);
          wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
        }
      }

      const ourKeywords = Array.from(wordCounts.entries())
        .map(([keyword, frequency]) => ({
          keyword,
          frequency,
          tfIdf: 0,
          density: (frequency / totalWords) * 100
        }));

      competitors.push({
        domain: 'our',
        url: contentAnalysis.our_domain.url || '',
        keywords: ourKeywords
      });
    }
  }

  // Process competitor content
  for (const comp of contentAnalysis.competitors || []) {
    if (!comp?.domain || !selectedDomains.includes(comp.domain)) continue;

    const text = [
      ...Object.values(comp.headers || {}).flat(),
      ...(comp.texts || [])
    ].join(' ').trim();

    if (!text) continue;

    const normalizedText = await normalizeText(text, language);
    const words = normalizedText.split(/\s+/);
    const totalWords = words.length;

    const wordCounts = new Map<string, number>();
    for (const word of words) {
      if (word && !(await isStopWord(word, language))) {
        allKeywords.add(word);
        wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
      }
    }

    const compKeywords = Array.from(wordCounts.entries())
      .map(([keyword, frequency]) => ({
        keyword,
        frequency,
        tfIdf: 0,
        density: (frequency / totalWords) * 100
      }));

    competitors.push({
      domain: comp.domain,
      url: comp.url,
      keywords: compKeywords
    });
  }

  // Calculate keyword frequencies
  const keywordFrequencies = new Map<string, number>();
  competitors.forEach(comp => {
    comp.keywords.forEach(({ keyword, frequency }) => {
      keywordFrequencies.set(
        keyword,
        (keywordFrequencies.get(keyword) || 0) + frequency
      );
    });
  });

  // Sort keywords
  const sortedKeywords = Array.from(allKeywords)
    .sort((a, b) => {
      const aIsAdditional = additionalKeywordsSet.has(a);
      const bIsAdditional = additionalKeywordsSet.has(b);
      
      if (aIsAdditional && !bIsAdditional) return -1;
      if (!aIsAdditional && bIsAdditional) return 1;
      
      return (keywordFrequencies.get(b) || 0) - (keywordFrequencies.get(a) || 0);
    });

  return { 
    competitors, 
    keywords: sortedKeywords,
    additionalKeywords: additionalKeywordsSet
  };
}

function TextAnalysisTab({ analysisId, contentAnalysis }: TextAnalysisTabProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [tableKey, setTableKey] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentContentAnalysis, setCurrentContentAnalysis] = useState(contentAnalysis);
  const [preparedData, setPreparedData] = useState<{
    competitors: any[];
    keywords: string[];
    additionalKeywords: Set<string>;
  }>({ competitors: [], keywords: [], additionalKeywords: new Set<string>() });
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [additionalKeywords, setAdditionalKeywords] = useState<string[]>([]);
  const [language, setLanguage] = useState<string>('uk');

  useEffect(() => {
    setCurrentContentAnalysis(contentAnalysis);
  }, [contentAnalysis]);

  useEffect(() => {
    // Load additional keywords and language
    const loadAdditionalKeywords = async () => {
      if (!analysisId) return;
      
      try {
        const { data, error } = await supabaseClient
          .from('analyses')
          .select('additional_keywords, language')
          .eq('id', analysisId)
          .single();
          
        if (error) throw error;
        
        if (data?.additional_keywords) {
          setAdditionalKeywords(data.additional_keywords);
        }
        
        if (data?.language) {
          setLanguage(data.language);
        }
      } catch (err) {
        console.error('Error loading additional keywords and language:', err);
      }
    };
    
    loadAdditionalKeywords();
  }, [analysisId]);

  useEffect(() => {
    const updatePreparedData = async () => {
      if (currentContentAnalysis) {
        const prepared = await prepareKeywordComparisonData(currentContentAnalysis, additionalKeywords, language);
        setPreparedData(prepared);
        setHasUnsavedChanges(true);
        
        // Auto-save if enabled
        if (autoSaveEnabled) {
          saveKeywordAnalysis(prepared);
        }
      }
    };
    updatePreparedData();
  }, [currentContentAnalysis, autoSaveEnabled, additionalKeywords, language]);

  const saveKeywordAnalysis = async (data: any) => {
    if (!data || !data.keywords || !data.competitors || !analysisId) return;
    
    try {
      setIsSaving(true);
      
      // Calculate metrics for each keyword
      const keywordMetrics: Record<string, any> = {};
      data.keywords.forEach((keyword: string) => {
        const ourContent = data.competitors.find((c: any) => c.domain === 'our');
        const ourKeyword = ourContent?.keywords.find((k: any) => k.keyword === keyword);
        
        const competitorData = data.competitors
          .filter((c: any) => c.domain !== 'our')
          .map((comp: any) => comp.keywords.find((k: any) => k.keyword === keyword))
          .filter(Boolean);

        const avgDensity = competitorData.reduce((sum: number, k: any) => sum + (k?.density || 0), 0) / 
          Math.max(1, competitorData.length);

        const ourDensity = ourKeyword?.density || 0;
        const densityRatio = avgDensity > 0 ? ourDensity / avgDensity : 0;
        const competitorCount = competitorData.filter((k: any) => k && k.frequency > 0).length;
        const totalCompetitors = data.competitors.filter((c: any) => c.domain !== 'our').length;
        
        const presencePercentage = totalCompetitors > 0 
          ? Math.round((competitorCount / totalCompetitors) * 100)
          : 0;

        const status = ourDensity > 0 ? 'Present' : 'Missing';

        keywordMetrics[keyword] = {
          keyword,
          density: ourDensity,
          avgDensity,
          densityRatio,
          competitorCount,
          totalCompetitors,
          presencePercentage,
          status
        };
      });
      
      // Prepare analysis data
      const analysisData = {
        keyword_analysis: {
          keywords: Object.values(keywordMetrics),
          metrics: {
            avgWordCount: 0,
            maxWordCount: 0,
            avgKeywordDensity: 0
          },
          lastUpdated: new Date().toISOString()
        }
      };

      const { error } = await supabaseClient
        .from('analyses')
        .update(analysisData)
        .eq('id', analysisId);

      if (error) throw error;
      
      setHasUnsavedChanges(false);
      console.log('Keyword analysis saved automatically');
    } catch (err) {
      console.error('Error saving keyword analysis:', err);
      // Don't show toast for auto-save failures to avoid spamming the user
      if (!autoSaveEnabled) {
        toast.error('Failed to save keyword analysis');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabaseClient
        .from('analyses')
        .select('*')
        .eq('id', analysisId)
        .single();

      if (fetchError) throw fetchError;
      setCurrentContentAnalysis(data?.content_analysis || null);
      
      // Also refresh additional keywords and language
      if (data?.additional_keywords) {
        setAdditionalKeywords(data.additional_keywords);
      }
      
      if (data?.language) {
        setLanguage(data.language);
      }
      
      const prepared = await prepareKeywordComparisonData(data?.content_analysis, data?.additional_keywords, data?.language || 'uk');
      setPreparedData(prepared);
      setTableKey(prev => prev + 1);
      
      toast.success('Analysis refreshed successfully');
    } catch (err) {
      console.error('Error refreshing analysis:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh analysis');
      toast.error('Failed to refresh analysis');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleManualSave = async () => {
    await saveKeywordAnalysis(preparedData);
    toast.success('Keyword analysis saved successfully');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Keyword Analysis</h2>
        <div className="flex gap-2">
          <div className="flex items-center mr-4">
            <input
              type="checkbox"
              id="auto-save"
              checked={autoSaveEnabled}
              onChange={(e) => setAutoSaveEnabled(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="auto-save" className="ml-2 text-sm text-gray-700">
              Auto-save
            </label>
          </div>
          {hasUnsavedChanges && !autoSaveEnabled && (
            <button
              onClick={handleManualSave}
              disabled={isSaving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              <Save className={`h-4 w-4 mr-2 ${isSaving ? 'animate-spin' : ''}`} />
              {isSaving ? 'Saving...' : 'Save Analysis'}
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Analysis'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {isAnalyzing && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center">
            <div className="mr-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            </div>
            <p className="text-sm text-blue-700">{progress}% complete</p>
          </div>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <KeywordComparisonTable 
          key={tableKey}
          competitors={preparedData.competitors}
          keywords={preparedData.keywords}
          additionalKeywords={preparedData.additionalKeywords}
          keywordImportance={
            analysisResult?.keywords.reduce((acc: any, k: any) => ({
              ...acc,
              [k.keyword]: k.importance
            }), {}) || {}
          }
          analysisId={analysisId}
          onSave={() => setHasUnsavedChanges(false)}
        />
      </div>
    </div>
  );
}

export default TextAnalysisTab;